import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button as MuiButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  ListItemIcon,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  LocationCity as LocationCityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Visibility as ViewResultsIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useApiClient } from '../../hooks/useApiClient';
import { useToast } from '../../context/ToastContext';
import KeywordsPanel from '../../components/serp/KeywordsPanel';
import BatchResultsDialog from '../../components/workflow-v2/BatchResultsDialog';
import CopyableText from '../../components/common/CopyableText';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const GOOGLE_DOMAINS = [
  { value: 'google.com', label: 'Google.com (US)' },
  { value: 'google.ca', label: 'Google.ca (Canada)' },
  { value: 'google.co.uk', label: 'Google.co.uk (UK)' },
  { value: 'google.com.au', label: 'Google.com.au (Australia)' },
  { value: 'google.de', label: 'Google.de (Germany)' },
  { value: 'google.fr', label: 'Google.fr (France)' },
  { value: 'google.co.in', label: 'Google.co.in (India)' },
  { value: 'google.co.jp', label: 'Google.co.jp (Japan)' },
  { value: 'google.com.br', label: 'Google.com.br (Brazil)' },
  { value: 'google.com.mx', label: 'Google.com.mx (Mexico)' },
];

const SerpToolStep: React.FC = () => {
  const { callApi } = useApiClient();
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const theme = useTheme();
  
  const [keywords, setKeywords] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [googleDomain, setGoogleDomain] = useState('google.com');
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const [statusPollingInterval, setStatusPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [lastRunConfig, setLastRunConfig] = useState<{
    keywords: string[];
    cities: string[];
  } | null>(null);
  const [configChanged, setConfigChanged] = useState(false);
  
  // Location search states
  const [locationSearchInput, setLocationSearchInput] = useState('');
  const [locationSearchValue, setLocationSearchValue] = useState<any>(null);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Search SerpAPI locations via backend proxy
  const searchLocations = async (searchQuery: string) => {
    if (!searchQuery) {
      setLocationOptions([]);
      return;
    }
    
    setIsLoadingLocations(true);
    try {
      const data = await callApi(`/api/v1/serp/locations?q=${encodeURIComponent(searchQuery)}&limit=10`, {
        method: 'GET'
      });
      setLocationOptions(data || []);
    } catch (error) {
      console.error('Error searching locations:', error);
      setLocationOptions([]);
      showToast('Failed to search locations', 'error');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Debounced location search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (locationSearchInput) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(locationSearchInput);
      }, 300);
    } else {
      setLocationOptions([]);
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [locationSearchInput]);

  // Load latest SERP config on mount
  useEffect(() => {
    const loadLatestConfig = async () => {
      if (!workflowId) return;
      
      setIsLoadingConfig(true);
      try {
        const response = await callApi(`/api/v1/workflow-v2/serp-analysis/config/${workflowId}`, {
          method: 'GET'
        });
        
        if (response.has_config && response.config) {
          const config = response.config;
          
          // Populate state with loaded config
          if (config.keywords && Array.isArray(config.keywords)) {
            setKeywords(config.keywords);
          }
          
          if (config.cities && Array.isArray(config.cities)) {
            setCities(config.cities);
            // Cities from config are already canonical names, no need to validate
          }
          
          if (config.google_domain) {
            setGoogleDomain(config.google_domain);
          }
          
          // Load batch job status if available
          if (response.batch_job) {
            setCurrentBatchId(response.batch_job.batch_id);
            setBatchStatus(response.batch_job);
            setShowProgressPanel(true);
            
            // Set as last run config if batch is completed
            if (response.batch_job.status === 'completed') {
              setLastRunConfig({
                keywords: config.keywords || [],
                cities: config.cities || []
              });
            }
            
            // If the batch is still processing AND actively running, start polling
            if (response.batch_job.status === 'processing' && response.batch_job.is_active_running) {
              setIsRunningAnalysis(true);
              
              const interval = setInterval(() => {
                pollBatchStatus(response.batch_job.batch_id);
              }, 2000); // Poll every 2 seconds
              
              pollingIntervalRef.current = interval;
              setStatusPollingInterval(interval);
              
              // Initial poll
              pollBatchStatus(response.batch_job.batch_id);
            }
          }
        }
      } catch (error: any) {
        console.error('Error loading SERP config:', error);
        // Don't show error toast - it's okay if there's no previous config
      } finally {
        setIsLoadingConfig(false);
      }
    };
    
    loadLatestConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]); // Run once when workflowId is available

  const handleLocationSelect = (event: any, value: any) => {
    if (value && value.canonical_name) {
      const canonicalName = value.canonical_name;
      
      // Check if location is already added
      if (cities.includes(canonicalName)) {
        showToast('Location already added', 'info');
        // Clear the search field
        setLocationSearchInput('');
        setLocationSearchValue(null);
        setLocationOptions([]);
        return;
      }
      
      // Add location to cities list
      const updatedCities = [...cities, canonicalName];
      setCities(updatedCities);
      
      // Clear the search input and value
      setLocationSearchInput('');
      setLocationSearchValue(null);
      setLocationOptions([]);
      
      showToast(`Added ${canonicalName}`, 'success');
    }
  };

  const handleRemoveCity = (index: number) => {
    setCities(cities.filter((_, i) => i !== index));
  };

  // Poll batch status
  const pollBatchStatus = async (batchId: string) => {
    try {
      const status = await callApi(`/api/v1/workflow-v2/serp-analysis/status/${batchId}`, {
        method: 'GET'
      });
      
      setBatchStatus(status);
      
      // Stop polling if completed, failed, cancelled, or not actively running
      const shouldStopPolling = 
        status.status === 'completed' || 
        status.status === 'failed' ||
        status.status === 'cancelled' ||
        (status.status === 'processing' && !status.is_active_running);
      
      if (shouldStopPolling) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setStatusPollingInterval(null);
        }
        setIsRunningAnalysis(false);
      }
    } catch (error: any) {
      console.error('Error polling batch status:', error);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Detect configuration changes
  useEffect(() => {
    if (!lastRunConfig) {
      setConfigChanged(false);
      return;
    }

    const hasChanges = 
      JSON.stringify(keywords.sort()) !== JSON.stringify(lastRunConfig.keywords.sort()) ||
      JSON.stringify(cities.sort()) !== JSON.stringify(lastRunConfig.cities.sort());

    setConfigChanged(hasChanges);
  }, [keywords, cities, lastRunConfig]);

  const handleResumeBatch = async (reprocessErrorsOnly: boolean = false) => {
    if (!currentBatchId) return;
    
    setIsResuming(true);
    
    try {
      const response = await callApi(`/api/v1/workflow-v2/serp-analysis/resume/${currentBatchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reprocess_errors_only: reprocessErrorsOnly
        })
      });

      if (response) {
        // Immediately fetch status to update UI
        await pollBatchStatus(currentBatchId);
        
        // Start polling for status updates
        const interval = setInterval(() => {
          pollBatchStatus(currentBatchId);
        }, 2000);
        
        pollingIntervalRef.current = interval;
        setStatusPollingInterval(interval);
        setIsRunningAnalysis(true);
        
        // Keep loading state true for a moment to ensure smooth transition
        setTimeout(() => {
          setIsResuming(false);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error resuming SERP analysis:', error);
      setIsResuming(false);
    }
  };

  const handleCancelBatch = async () => {
    if (!currentBatchId) return;
    
    setShowCancelDialog(true);
  };

  const confirmCancelBatch = async () => {
    setShowCancelDialog(false);
    if (!currentBatchId) return;
    
    setIsCancelling(true);
    try {
      const response = await callApi(`/api/v1/workflow-v2/batch-job/cancel/${currentBatchId}`, {
        method: 'POST'
      });

      if (response) {
        showToast('SERP Analysis cancelled successfully', 'info');
        
        // Stop polling immediately
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setStatusPollingInterval(null);
        }
        setIsRunningAnalysis(false);
        
        // Fetch final status
        await pollBatchStatus(currentBatchId);
      }
    } catch (error: any) {
      console.error('Error cancelling SERP analysis:', error);
      showToast(error.message || 'Failed to cancel SERP analysis', 'error');
    } finally {
      setIsCancelling(false);
    }
  };


  const handleRunSerpAnalysis = async () => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }

    if (keywords.length === 0) {
      showToast('Please add at least one query to scrape', 'error');
      return;
    }

    // Hide previous result panel immediately
    setShowProgressPanel(false);
    setBatchStatus(null);
    setIsRunningAnalysis(true);
    
    try {
      // Cities are already canonical names from the autocomplete search
      const citiesToSend = cities.length > 0 ? cities : null;
      
      const response = await callApi('/api/v1/workflow-v2/serp-analysis/start', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          keywords: keywords,
          cities: citiesToSend,
          google_domain: googleDomain,
          num_results: 20, // Default to 20 results
          rate_limit_ms: 100 // Default rate limit
        })
      });

      if (response) {
        setCurrentBatchId(response.batch_id);
        setShowProgressPanel(true);
        setBatchStatus({
          batch_id: response.batch_id,
          status: 'processing',
          progress: {
            total_queries: response.total_queries,
            processed_queries: 0,
            successful_queries: 0,
            failed_queries: 0,
            percentage: 0
          }
        });
        
        // Save current config as last run config (use canonical names that were sent)
        setLastRunConfig({
          keywords: [...keywords],
          cities: citiesToSend || []
        });
        setConfigChanged(false);
        
        showToast(
          `SERP Analysis started! Processing ${response.total_queries} queries.`,
          'success'
        );
        
        // Start polling for status updates
        const interval = setInterval(() => {
          pollBatchStatus(response.batch_id);
        }, 2000); // Poll every 2 seconds
        
        pollingIntervalRef.current = interval;
        setStatusPollingInterval(interval);
        
        // Initial poll
        pollBatchStatus(response.batch_id);
      }
    } catch (error: any) {
      console.error('Error starting SERP analysis:', error);
      showToast(error.message || 'Failed to start SERP analysis', 'error');
      setIsRunningAnalysis(false);
    }
  };

  // Show loading spinner during config loading
  if (isLoadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading SERP configuration...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
      Configure query samples to send to SERP API for SERP scraping.
      </Typography>

      {/* Keywords and Cities Side by Side */}
      <Grid container spacing={3}>
        {/* Keywords Panel */}
        <Grid item xs={12} md={6}>
          <KeywordsPanel
            clientId={clientId}
            workflowId={workflowId}
            keywords={keywords}
            onKeywordsChange={setKeywords}
          />
        </Grid>

        {/* Cities Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationCityIcon color="action" />
                <Typography variant="h6">
                  Locations (Optional)
                </Typography>
                {cities.length > 0 && (
                  <Chip 
                    label={cities.length} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search and add locations (cities, postal codes, zip codes, etc.) to target specific geographic areas. 
                Locations are used as geo-location parameters, not appended to keywords. The canonical names from SERP API will be used for accurate targeting.
              </Typography>

              <Autocomplete
                value={locationSearchValue}
                options={locationOptions}
                loading={isLoadingLocations}
                inputValue={locationSearchInput}
                onInputChange={(event, newInputValue) => {
                  setLocationSearchInput(newInputValue);
                }}
                onChange={handleLocationSelect}
                getOptionLabel={(option: any) => {
                  if (typeof option === 'string') return option;
                  return option.canonical_name || option.name || '';
                }}
                renderOption={(props, option: any) => (
                  <Box component="li" {...props} key={option.id || option.canonical_name}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.canonical_name || option.name}
                      </Typography>
                      {option.reach && (
                        <Typography variant="caption" color="text.secondary">
                          {option.reach.toLocaleString()} people
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for a location (e.g., Toronto, 90210, New York, M5H 2N2)"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="No locations found"
                loadingText="Searching locations..."
                openOnFocus
                sx={{ mb: 2 }}
              />
              
              {cities.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Selected Locations ({cities.length}):
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {cities.map((city, index) => (
                        <ListItem key={index}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <LocationCityIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={city}
                            primaryTypographyProps={{
                              sx: { fontSize: '0.9rem' }
                            }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveCity(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SERP Analysis Configuration Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Summary Section */}
          {keywords.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Request Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 1.5, 
                    flex: '1 1 auto', 
                    minWidth: 120,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                    {keywords.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Queries to scrape
                  </Typography>
                </Paper>
                
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 1.5, 
                    flex: '1 1 auto', 
                    minWidth: 120,
                    backgroundColor: alpha(theme.palette.success.main, 0.05),
                    border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`
                  }}
                >
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                    {keywords.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total SERP Queries
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Configuration Changed Alert - Hide when analysis is running */}
          {configChanged && lastRunConfig && !isRunningAnalysis && !batchStatus?.is_active_running && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3,
                borderLeft: 4,
                borderColor: 'warning.main'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Request Parameters Changed
              </Typography>
              <Typography variant="body2">
                You have modified queries to scrape or cities since the last analysis. 
                Please run a new SERP analysis to see updated results.
              </Typography>
            </Alert>
          )}
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Google Domain</InputLabel>
                <Select
                  value={googleDomain}
                  onChange={(e) => setGoogleDomain(e.target.value)}
                  label="Google Domain"
                >
                  {GOOGLE_DOMAINS.map((domain) => (
                    <MenuItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <MuiButton
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={
                  keywords.length === 0 || 
                  isRunningAnalysis ||
                  (batchStatus && batchStatus.status === 'processing' && batchStatus.is_active_running)
                }
                onClick={handleRunSerpAnalysis}
                startIcon={
                  isRunningAnalysis ? <CircularProgress size={20} /> : undefined
                }
                sx={{ height: '56px' }}
              >
                {(isRunningAnalysis || (batchStatus && batchStatus.status === 'processing' && batchStatus.is_active_running))
                  ? 'Analysis Running...' 
                  : 'Run SERP Analysis'}
              </MuiButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Progress Status Panel */}
      {showProgressPanel && batchStatus && (
        <Card 
          sx={{ 
            mt: 3,
            borderLeft: 4, 
            borderColor: 
              batchStatus.status === 'completed' ? 'success.main' : 
              batchStatus.status === 'failed' ? 'error.main' :
              batchStatus.status === 'cancelled' ? 'grey.500' :
              'primary.main'
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">
                  SERP Analysis Progress
                </Typography>
                <Chip 
                  label={batchStatus.status.toUpperCase()} 
                  color={
                    batchStatus.status === 'completed' ? 'success' : 
                    batchStatus.status === 'failed' ? 'error' : 
                    batchStatus.status === 'cancelled' ? 'default' :
                    'primary'
                  }
                  size="small"
                />
                {batchStatus.batch_id && (
                  <Tooltip title="Review batch results" arrow>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setResultsDialogOpen(true)}
                      sx={{ 
                        padding: 0.5,
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                      }}
                    >
                      <ViewResultsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <CopyableText text={batchStatus.batch_id} label="Batch ID" />
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {batchStatus.progress.processed_queries} / {batchStatus.progress.total_queries} queries processed
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {batchStatus.progress.percentage}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={batchStatus.progress.percentage} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            {/* Statistics Grid */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {batchStatus.progress.total_queries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Queries
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    {batchStatus.progress.successful_queries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">
                    {batchStatus.progress.failed_queries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </Paper>
              </Grid>
              {batchStatus.status !== 'completed' && (
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {isRunningAnalysis && <CircularProgress size={16} />}
                      <Typography variant="h6">
                        {batchStatus.timing?.estimated_seconds_remaining 
                          ? `${Math.floor(batchStatus.timing.estimated_seconds_remaining / 60)}m ${batchStatus.timing.estimated_seconds_remaining % 60}s`
                          : '-'
                        }
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Est. Remaining
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* Error Message */}
            {batchStatus.error_message && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {batchStatus.error_message}
              </Alert>
            )}

            {/* Show info if batch is actively running */}
            {batchStatus.status === 'processing' && batchStatus.is_active_running && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch is actively processing
                </Typography>
                <Typography variant="body2">
                  The batch is currently running in the background. Progress updates will appear automatically.
                </Typography>
              </Alert>
            )}

            {/* Show warning if status is processing but not actively running */}
            {batchStatus.status === 'processing' && !batchStatus.is_active_running && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch appears to be stuck
                </Typography>
                <Typography variant="body2">
                  The batch is marked as processing but is not actively running. 
                  It may have been interrupted by a server restart or crash.
                  You can resume to restart processing from where it stopped.
                </Typography>
              </Alert>
            )}

            {/* Action Buttons - Resume and Cancel */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              {/* Resume Button - Show when not actively running and not completed */}
              {!batchStatus.is_active_running && 
               batchStatus.status !== 'completed' && (
                <MuiButton
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    isResuming 
                      ? <CircularProgress size={20} color="inherit" /> 
                      : <ReplayIcon />
                  }
                  onClick={() => handleResumeBatch(false)}
                  disabled={isResuming || isCancelling}
                  sx={{ minWidth: 200 }}
                >
                  {isResuming ? 'Starting...' : 'Resume Batch Process'}
                </MuiButton>
              )}
              
              {/* Cancel Button - Show when actively running */}
              {batchStatus.status === 'processing' && batchStatus.is_active_running && (
                <MuiButton
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={
                    isCancelling 
                      ? <CircularProgress size={20} color="inherit" /> 
                      : <DeleteIcon />
                  }
                  onClick={handleCancelBatch}
                  disabled={isCancelling || isResuming}
                  sx={{ minWidth: 200 }}
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel Batch Process'}
                </MuiButton>
              )}
            </Box>

            {/* Completion Message */}
            {batchStatus.status === 'completed' && (
              <>
                {/* Only show success message if there's at least 1 successful query */}
                {batchStatus.progress.successful_queries > 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      SERP Analysis completed successfully! {batchStatus.progress.successful_queries} out of {batchStatus.progress.total_queries} queries were successful.
                    </Typography>
                  </Alert>
                )}
                
                {/* Reprocess Failed Queries Button - Show when completed with failures */}
                {batchStatus.progress.failed_queries > 0 && !batchStatus.is_active_running && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <MuiButton
                      variant="outlined"
                      color="warning"
                      size="large"
                      startIcon={
                        isResuming 
                          ? <CircularProgress size={20} color="inherit" /> 
                          : <ReplayIcon />
                      }
                      onClick={() => handleResumeBatch(true)}
                      disabled={isResuming}
                      sx={{ minWidth: 250 }}
                    >
                      {isResuming 
                        ? 'Starting...' 
                        : `Reprocess ${batchStatus.progress.failed_queries} Failed Queries`}
                    </MuiButton>
                  </Box>
                )}
              </>
            )}

            {/* Cancellation Message */}
            {batchStatus.status === 'cancelled' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  SERP Analysis was cancelled. {batchStatus.progress.successful_queries} out of {batchStatus.progress.total_queries} queries were completed before cancellation.
                </Typography>
              </Alert>
            )}

            {/* Info - Only show when not completed and not cancelled */}
            {batchStatus.status !== 'completed' && batchStatus.status !== 'cancelled' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This process may take several minutes to hours depending on the number of queries.
                  You can safely close this page - the processing will continue in the background.
                  {(batchStatus.status === 'failed' || (batchStatus.status === 'processing' && !batchStatus.is_active_running)) && 
                    ' If processing is interrupted, you can resume from where it stopped using the Resume button above.'}
                </Typography>
              </Alert>
            )}

            {/* Proceed to Data For SEO Volume Analysis Button - Show when completed */}
            {batchStatus.status === 'completed' && (
              <Box sx={{ mt: 3 }}>
                <MuiButton
                  variant="contained"
                  color="success"
                  size="large"
                  fullWidth
                  onClick={() => navigate(`/workflow2/${clientId}/${workflowId}/data_for_seo`)}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  Proceed to Data For SEO Volume Analysis →
                </MuiButton>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Batch Results Dialog */}
      {batchStatus?.batch_id && (
        <BatchResultsDialog
          open={resultsDialogOpen}
          onClose={() => setResultsDialogOpen(false)}
          batchId={batchStatus.batch_id}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        title="Cancel SERP Analysis"
        message="Are you sure you want to cancel this SERP analysis? Currently processing queries will complete, but no new queries will be started. This action cannot be undone."
        confirmText="Yes, Cancel Analysis"
        cancelText="No, Keep Running"
        confirmColor="error"
        onConfirm={confirmCancelBatch}
        onCancel={() => setShowCancelDialog(false)}
      />
    </Box>
  );
};

export default SerpToolStep;

